import { effect, inject, Injectable, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { RECORDING_RETENTION_MS } from '@andor/contracts/recording';
import { RecordingService as RecordingApi } from '../../shared/api/generated/recording/recording.service';
import { transcriptBacklogResponseSchema } from '../../shared/schemas/backend-api';
import { CortexRoomFrame, parseCortexTranscript } from '../../shared/schemas/cortex-room-frame';
import { sleep } from '../../shared/helpers/async';
import { RoomsStore } from '../../state/rooms.repository';
import {
  TranscriptLine,
  TranscriptsStore,
  TranscriptSessionMeta,
} from '../../state/transcripts.repository';
import { isMaintenancePhase, MaintenanceService } from './maintenance.service';
import {
  CortexFrameEvent,
  MediaRejoinedEvent,
  OdinService,
  TranscriptionBotEvent,
} from './odin.service';

/** Wait before the first binding fetch, so a burst of early frames shares it. */
const BIND_DELAY_MS = 400;
/** Backoff of the binding fetch; past the last step the connection gives up. */
const BIND_RETRY_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
/**
 * How long after the bot appeared a `404 NOT_RECORDING` still means "not yet":
 * the bot reaches the room before the start route commits the recording.
 */
const CLAIM_GRACE_MS = 30_000;
/** Lines kept per connection while it is not bound yet; the oldest go first. */
const MAX_BUFFERED_LINES = 200;
/**
 * When to fetch an ended recording's transcript, after its end. Cortex keeps
 * transcribing utterances queued when the bot left and writes them without a
 * frame; one provider call can take a minute, and a long queue several. The
 * last fetch stays well inside the retention window (`RECORDING_RETENTION_MS`).
 */
const FINAL_FETCH_DELAYS_MS = [0, 10_000, 60_000, 5 * 60_000];
const FETCH_TIMEOUT_MS = 15_000;
// Give the server a moment to process our `room-rejoin` (presence re-attach)
// after a maintenance pause before fetching — an immediate fetch after an
// outage longer than the presence TTL would 403 (NOT_IN_ROOM).
const RESUME_HEAL_DELAY_MS = 1_000;
const RESUME_HEAL_RETRY_MS = 4_000;
/** The server keeps an interrupted recording's transcript this long after resume. */
const RESUME_CATCH_UP_MS = 5 * 60_000;

/**
 * The transcript line a frame carries, or `null`. `segmentId` is the Cortex
 * message id the backlog returns too, and `ts` the same message timestamp, so
 * frame and backlog lines dedupe and sort together.
 */
function transcriptLineOf(frame: CortexRoomFrame): TranscriptLine | null {
  const segment = parseCortexTranscript(frame);
  if (!segment) return null;
  return {
    id: segment.segmentId,
    senderName: segment.senderName,
    content: segment.text,
    timestamp: segment.ts,
  };
}

/** Which copy a backlog fetch reads; by default the room's current one. */
interface BacklogSelector {
  /** A maintenance operation's retained copy. */
  maintenanceOperation?: string;
  /** Only the recording that started then (epoch ms), running or retained. */
  startedAt?: number;
}

/** What one backlog fetch did. */
type BacklogResult =
  /** Merged into its own session; `ids` are the lines it held. */
  | { kind: 'merged'; meta: TranscriptSessionMeta; ids: ReadonlySet<string> }
  | { kind: 'notRecording' }
  | { kind: 'forbidden' }
  | { kind: 'failed' };

/**
 * One connection of the transcription bot in the held room. Cortex frames
 * carry no session identity (`startedAt`), so the connection's lines wait in
 * `buffer` until a backlog response proves which recording they belong to:
 * one that contains a buffered line. A plain "the backlog answered" is not
 * enough, because right after a start the backlog still serves the previous
 * recording's final copy until the new recording is committed.
 */
interface BotConnection {
  roomId: string;
  peerId: number;
  /** When this client first saw the connection (its join or first frame). */
  firstSeenAt: number;
  /** The recording's session once bound; lines then go straight to the store. */
  meta: TranscriptSessionMeta | null;
  buffer: TranscriptLine[];
  bindAttempts: number;
  bindTimer?: ReturnType<typeof setTimeout>;
  /**
   * The binding attempt in flight. One at a time: frames arriving meanwhile
   * only join the buffer, and the attempt makes one retry decision.
   */
  binding?: Promise<void>;
  /** Binding is over for good (not admitted, or never recorded): drop lines. */
  gaveUp: boolean;
}

/**
 * Owns the local transcript of recordings in the held room.
 *
 * Live lines come straight from the transcription bot peer: OdinService hands
 * on its Cortex Room Protocol frames, and this service files their lines under
 * the recording's session in `TranscriptsStore`. Frames may miss lines — while
 * this client joins or reconnects, during a media key switch, or while the
 * bot's own connection drops — and that is accepted: live lines are a
 * convenience. The backlog (`GET /v1/notifications/transcript`) is the source
 * of truth, and this service completes the stored copy from it when it
 * matters: seeding a late joiner, when a recording ended (the bot left, also
 * while this client was reconnecting, or this client left the room), before a
 * download, and after a maintenance pause. A recording's final fetches name it
 * by its `startedAt`, so the next recording in the room never takes them over,
 * and repeat for a few minutes while Cortex finishes its last utterances.
 *
 * It also sets the deletion deadline the panel counts down: the server deletes
 * the transcript `RECORDING_RETENTION_MS` after the bot's live leave.
 */
@Injectable({ providedIn: 'root' })
export class TranscriptService {
  readonly #odin = inject(OdinService);
  readonly #recordingApi = inject(RecordingApi);
  readonly #transcripts = inject(TranscriptsStore);
  readonly #roomsStore = inject(RoomsStore);
  readonly #maintenance = inject(MaintenanceService);

  #connection: BotConnection | null = null;
  /**
   * The recording this client attends, from the bot's first appearance to its
   * live leave. It survives reconnects and bot rejoins, so `since` still
   * identifies the recording's transcript when it ends.
   */
  #episode: { roomId: string; since: number } | null = null;
  /** Single flight per room and operation. */
  readonly #inFlight = new Map<string, Promise<BacklogResult>>();
  /** Maintenance operations that interrupted a meeting, with its room. */
  readonly #interrupted = new Map<string, string>();

  constructor() {
    this.#odin.transcriptionBotEvents$.subscribe((event) => this.#onBotEvent(event));
    this.#odin.cortexFrames$.subscribe((event) => this.#onFrame(event));
    this.#odin.mediaRejoined$.subscribe((event) => this.#onMediaRejoined(event));

    // Leaving (or switching) a recorded room: complete what this client keeps
    // of it while its admission still allows the fetch.
    let heldRoom: string | null = null;
    effect(() => {
      const roomId = this.#odin.joinedRoomId();
      untracked(() => {
        if (roomId === heldRoom) return;
        const left = heldRoom;
        heldRoom = roomId;
        if (left !== null && this.#episode?.roomId === left) {
          const startedAt = this.#connection?.meta?.startedAt;
          this.#endEpisode();
          this.#complete(left, startedAt);
        }
      });
    });

    this.#maintenance.changes.subscribe((status) => {
      if (
        status.available ||
        !isMaintenancePhase(status.phase) ||
        !status.operation ||
        this.#interrupted.has(status.operation) ||
        !this.#odin.room
      )
        return;
      const roomId = this.#odin.currentRoomId;
      if (roomId) this.#interrupted.set(status.operation, roomId);
    });
    this.#maintenance.resumed.subscribe(() => this.#onResumed());
  }

  /**
   * Fetch the room's backlog now and merge it, joining a fetch already in
   * flight. Returns whether a transcript was merged. Used before a download.
   */
  async heal(roomId: string): Promise<boolean> {
    return (await this.#fetch(roomId)).kind === 'merged';
  }

  #onBotEvent(event: TranscriptionBotEvent): void {
    if (event.kind === 'left') {
      if (this.#connection?.peerId !== event.peerId) return;
      this.#recordingEnded(event.roomId);
      return;
    }
    const connection = this.#connectionFor(event.roomId, event.peerId);
    // Joined mid-recording or reloaded: earlier lines exist only in the
    // backlog. A live join is a fresh recording; its first frame binds.
    if (!event.live) void this.#bind(connection);
  }

  #onFrame({ roomId, botPeerId, frame }: CortexFrameEvent): void {
    const line = transcriptLineOf(frame);
    if (!line) return;
    const connection = this.#connectionFor(roomId, botPeerId);
    if (connection.meta) {
      this.#transcripts.appendMessages(connection.meta, [line]);
      return;
    }
    if (connection.gaveUp) return;
    connection.buffer.push(line);
    if (connection.buffer.length > MAX_BUFFERED_LINES) connection.buffer.shift();
    this.#scheduleBind(connection, BIND_DELAY_MS);
  }

  /**
   * The media came back. The peer replay before it decides: a bot still
   * present continues (its frames are missing a few lines, which the final
   * fetch restores); a bot gone means the recording ended while we were
   * away — its leave never reached us, so complete the transcript now.
   */
  #onMediaRejoined({ roomId, botPresent }: MediaRejoinedEvent): void {
    if (botPresent || this.#episode?.roomId !== roomId) return;
    const startedAt = this.#connection?.meta?.startedAt;
    this.#endEpisode();
    this.#complete(roomId, startedAt);
  }

  /** The current bot connection, or a new one (and episode) for this bot. */
  #connectionFor(roomId: string, peerId: number): BotConnection {
    if (this.#episode?.roomId !== roomId) {
      // Switched straight into another recorded room before the held-room
      // effect ran: complete the one we left first.
      if (this.#episode) {
        const left = this.#episode.roomId;
        const startedAt =
          this.#connection?.roomId === left ? this.#connection.meta?.startedAt : undefined;
        this.#complete(left, startedAt);
      }
      this.#episode = { roomId, since: Date.now() };
      // An end still waiting for its session belongs to the previous
      // recording; it must not stick to this one.
      this.#transcripts.forgetPendingEnd(roomId);
    }
    const current = this.#connection;
    if (current && current.roomId === roomId && current.peerId === peerId) return current;
    if (current?.bindTimer !== undefined) clearTimeout(current.bindTimer);
    const connection: BotConnection = {
      roomId,
      peerId,
      firstSeenAt: Date.now(),
      meta: null,
      buffer: [],
      bindAttempts: 0,
      gaveUp: false,
    };
    this.#connection = connection;
    return connection;
  }

  /** The bot left live: mark the deletion deadline and complete the transcript. */
  #recordingEnded(roomId: string): void {
    // The server schedules the deletion only after the bot left, so this
    // deadline is never later than the real deletion.
    this.#transcripts.markRecordingEnded(
      roomId,
      Date.now() + RECORDING_RETENTION_MS,
      this.#episode?.since ?? Date.now()
    );
    const startedAt = this.#connection?.meta?.startedAt;
    this.#endEpisode();
    this.#complete(roomId, startedAt);
  }

  /**
   * Complete an ended recording's stored copy: fetch it now and again at
   * {@link FINAL_FETCH_DELAYS_MS}. The fetches name the recording by
   * `startedAt`, so a recording started in the room meanwhile never takes
   * them over. A recording whose frames were never bound is named by the
   * first fetch's answer instead. Fetches the server refuses later (the
   * client left the room, or the transcript is deleted) just merge nothing.
   */
  #complete(roomId: string, startedAt: number | undefined): void {
    void (async () => {
      const first = await this.#fetch(roomId, { startedAt });
      const target = startedAt ?? (first.kind === 'merged' ? first.meta.startedAt : undefined);
      for (const delay of FINAL_FETCH_DELAYS_MS.slice(1)) {
        setTimeout(() => void this.#fetch(roomId, { startedAt: target }), delay);
      }
    })();
  }

  #endEpisode(): void {
    if (this.#connection?.bindTimer !== undefined) clearTimeout(this.#connection.bindTimer);
    this.#connection = null;
    this.#episode = null;
  }

  #scheduleBind(connection: BotConnection, delayMs: number): void {
    // An attempt in flight decides on its own retry once it completes.
    if (connection.bindTimer !== undefined || connection.binding) return;
    connection.bindTimer = setTimeout(() => {
      connection.bindTimer = undefined;
      void this.#bind(connection);
    }, delayMs);
  }

  /**
   * Bind the connection, joining the attempt already in flight. A failed
   * attempt schedules exactly one retry, however many frames arrived while it
   * was pending.
   */
  #bind(connection: BotConnection): Promise<void> {
    if (connection.meta || connection.gaveUp) return Promise.resolve();
    connection.binding ??= this.#attemptBind(connection).then((retryIn) => {
      connection.binding = undefined;
      if (retryIn !== undefined) this.#scheduleBind(connection, retryIn);
    });
    return connection.binding;
  }

  /**
   * Fetch the backlog and bind the connection if the response holds one of
   * its buffered lines. Resolves to the delay of the next attempt while that
   * can still change the outcome, or `undefined` when binding is done.
   */
  async #attemptBind(connection: BotConnection): Promise<number | undefined> {
    const result = await this.#fetch(connection.roomId);
    if (this.#connection !== connection || connection.meta || connection.gaveUp) return;
    switch (result.kind) {
      case 'merged':
        if (connection.buffer.some((line) => result.ids.has(line.id))) {
          connection.meta = result.meta;
          this.#transcripts.appendMessages(result.meta, connection.buffer);
          connection.buffer = [];
          return;
        }
        // A seed with nothing buffered yet: the first frame binds.
        if (connection.buffer.length === 0) return;
        break;
      case 'notRecording':
        if (Date.now() - connection.firstSeenAt >= CLAIM_GRACE_MS) {
          this.#giveUp(connection);
          return;
        }
        break;
      case 'forbidden':
        this.#giveUp(connection);
        return;
      case 'failed':
        break;
    }
    const delay = BIND_RETRY_MS[connection.bindAttempts++];
    if (delay === undefined) this.#giveUp(connection);
    return delay;
  }

  #giveUp(connection: BotConnection): void {
    connection.gaveUp = true;
    connection.buffer = [];
  }

  /**
   * Fetch a room's backlog (or a maintenance operation's retained copy, or
   * only the recording that started at `startedAt`) and merge it into its own
   * session. Single flight per room, operation and recording.
   */
  #fetch(roomId: string, selector: BacklogSelector = {}): Promise<BacklogResult> {
    const key = [roomId, selector.maintenanceOperation ?? '', selector.startedAt ?? ''].join('\n');
    let pending = this.#inFlight.get(key);
    if (!pending) {
      pending = this.#load(roomId, selector).finally(() => this.#inFlight.delete(key));
      this.#inFlight.set(key, pending);
    }
    return pending;
  }

  async #load(
    roomId: string,
    { maintenanceOperation, startedAt: recording }: BacklogSelector
  ): Promise<BacklogResult> {
    try {
      const res = await firstValueFrom(
        this.#recordingApi
          .getTranscript({
            roomId,
            ...(maintenanceOperation && { maintenanceOperation }),
            ...(recording !== undefined && { startedAt: String(recording) }),
          })
          .pipe(timeout(FETCH_TIMEOUT_MS))
      );
      const parsed = transcriptBacklogResponseSchema.safeParse(res);
      if (!parsed.success) return { kind: 'failed' };
      const { startedAt, startedByName, messages } = parsed.data;
      const roomName = this.#roomsStore.rooms.find((r) => r.roomId === roomId)?.name ?? roomId;
      const meta: TranscriptSessionMeta = { roomId, roomName, startedByName, startedAt };
      this.#transcripts.appendMessages(meta, messages);
      return { kind: 'merged', meta, ids: new Set(messages.map((m) => m.id)) };
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404 && error.error?.code === 'NOT_RECORDING') {
          return { kind: 'notRecording' };
        }
        if (error.status === 403) return { kind: 'forbidden' };
      }
      return { kind: 'failed' };
    }
  }

  /**
   * After a pause: fetch the retained transcript of every recording the pause
   * ended in a meeting we are still in, until the server's recovery window
   * closes. And give an unbound connection another chance to bind.
   */
  #onResumed(): void {
    const connection = this.#connection;
    if (connection && !connection.meta) {
      connection.gaveUp = false;
      connection.bindAttempts = 0;
      if (connection.buffer.length) this.#scheduleBind(connection, RESUME_HEAL_DELAY_MS);
    }
    for (const [operation, roomId] of this.#interrupted) {
      void (async () => {
        const deadline = Date.now() + RESUME_CATCH_UP_MS;
        // Presence is reissued before recovery completes; allow its write to settle.
        await sleep(RESUME_HEAL_DELAY_MS);
        while (Date.now() < deadline && !this.#maintenance.active()) {
          if (this.#odin.currentRoomId !== roomId) break;
          const result = await this.#fetch(roomId, { maintenanceOperation: operation });
          // A missing operation record cannot appear later: nothing to catch up.
          if (result.kind === 'merged' || result.kind === 'notRecording') break;
          await sleep(RESUME_HEAL_RETRY_MS);
        }
        if (!this.#maintenance.active()) this.#interrupted.delete(operation);
      })();
    }
  }
}
